import * as Sentry from '@sentry/nextjs';
import { LDFlagSet } from 'launchdarkly-node-server-sdk';
import _ from 'lodash';
import { DiagnosticFields } from '../../dto/diagnostics';
import { EventFlow, EventType } from '../../dto/events';
import { LAYER_EVENT_SEND_ALLOW_LIST } from '../../events/mapping';
import { redact } from './redactor';

const { NODE_ENV } = process.env;

export type Correlator = {
  /**
   * Returns the current correlation id (if defined)
   * or a new correlation id.
   *
   * `onCompleteFlow` should be called when the consumer is done
   * using the correlation id (e.g. the corresponding action has completed).
   * An implementation may clear out the correlation id
   * so that a new one is issued upon the next call
   */
  getCurrentOrGenerateNewId: () => {
    correlationId: string;
    onCompleteFlow: () => void;
  };
  getCurrentId: () => string;
};

export const toActionName = (layer: EventType, flow: EventFlow) =>
  `${_.upperFirst(_.camelCase(layer))}.${_.upperFirst(_.camelCase(flow))}`;

export const generateDataPoint = (
  layer: EventType,
  flow: EventFlow,
  params?: Record<string, any> | DiagnosticFields,
  error?: string
) => ({
  layer,
  flow,
  action: toActionName(layer, flow),
  ...params,
  error,
});

export type DiagnosticsLogger = {
  error: (dp: DiagnosticFields) => void;
  debug: (dp: DiagnosticFields) => void;
};

export type EventSender = {
  sendEvent: (dp: DiagnosticFields) => Promise<boolean>;
};
const sendDataPointToEvent = async (
  layer: EventType,
  flow: EventFlow,
  dp: DiagnosticFields,
  eventSender: EventSender
) => {
  const failureErrorTitle = `Failure: ${toActionName(
    layer,
    flow
  )} Could not publish event`;
  try {
    if (!(await eventSender.sendEvent(dp))) {
      const e = new Error(failureErrorTitle);
      Sentry.captureException(e);
    }
  } catch (err) {
    const e = new Error(failureErrorTitle);

    e['cause'] = err;
    Sentry.captureException(e);
  }
};

export const withDataPointInternal = async <T = void>(
  layer: EventType,
  flow: EventFlow,
  func: (dp: DiagnosticFields) => Promise<T>,
  correlator: Correlator,
  logger: DiagnosticsLogger,
  eventSender: EventSender,
  ldFlags: LDFlagSet
): Promise<T> => {
  const dp = generateDataPoint(layer, flow, {
    start_time: Date.now(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    feature_flags: { flags: { ldFlags } },
  }) as DiagnosticFields;

  const { correlationId, onCompleteFlow } =
    correlator.getCurrentOrGenerateNewId();

  let result: T;
  try {
    result = await func(dp);
  } catch (err) {
    dp.error = JSON.stringify(err, Object.getOwnPropertyNames(err));
    throw err;
  } finally {
    dp.correlation_id = correlationId;
    dp.duration = Date.now() - dp.start_time;
    dp.success = false;
    if (dp.error) {
      const redacted = redact(dp);
      logger.error(redacted);
      const e = new Error(JSON.stringify(redacted));
      e.name = `Failure: ${toActionName(layer, flow)}`;
      Sentry.captureException(e);
    } else {
      // TODO: A void function returns undefined,
      // but the fact that it completed without error
      // should be considered a success
      if (result || result === undefined) {
        dp.success = true;
      }
      logger.debug(redact(dp));
    }

    onCompleteFlow();

    if (LAYER_EVENT_SEND_ALLOW_LIST.includes(layer) && NODE_ENV !== 'test') {
      await sendDataPointToEvent(layer, flow, dp, eventSender);
    }
  }
  return result;
};
